import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import useSubscriptionApi from '../../hooks/useSubscriptionApi';
import PlanCard from './PlanCard';
import CurrentSubscriptionCard from './CurrentSubscriptionCard';
import BillingPreferenceModal from './BillingPreferenceModal';
import { SUBSCRIPTION_STATUS } from '../../utils/constants';
import '../../styles/SubscriptionPlans.css';

/**
 * SubscriptionPlans Component
 *
 * Main subscription plans page showing:
 * - Current subscription status (if any)
 * - Horizontal carousel of plans (sorted by price, cheapest first)
 * - Plan selection/change/trial functionality
 */
const SubscriptionPlans = () => {
    const { t } = useTranslation(['subscription', 'common']);
    const navigate = useNavigate();

    // Context
    const {
        subscriptionData,
        refreshSubscriptionStatus,
        getSubscriptionStatusText,
        formatDate,
        isInGracePeriod,
        canStartTrial
    } = useSubscription();

    // API hook
    const {
        error: apiError,
        clearError,
        fetchPlans,
        createCheckoutSession,
        upgradePlan,
        openCustomerPortal,
        startTrial,
        activateFree,
        validatePromoCode
    } = useSubscriptionApi();

    // Local state
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPlan, setProcessingPlan] = useState(null);
    // Billing preference modal state
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [selectedPlanForPurchase, setSelectedPlanForPurchase] = useState(null);

    // Promo code state
    const [promoCode, setPromoCode] = useState('');
    const [promoValidation, setPromoValidation] = useState(null);
    const [promoValidating, setPromoValidating] = useState(false);

    // Sync API errors to local error state
    useEffect(() => {
        if (apiError) {
            setError(apiError);
        }
    }, [apiError]);

    // Fetch plans on mount
    useEffect(() => {
        loadPlans();
    }, []);

    // Check scroll state
    /**
     * Load subscription plans
     */
    const loadPlans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedPlans = await fetchPlans();
            setPlans(fetchedPlans);
        } catch (err) {
            // Error already handled by hook
        } finally {
            setLoading(false);
        }
    }, [fetchPlans]);

    /**
     * Scroll carousel left
     */
    /**
     * Handle promo code validation
     */
    const handleValidatePromoCode = useCallback(async () => {
        if (!promoCode.trim()) return;
        setPromoValidating(true);
        setPromoValidation(null);
        try {
            const result = await validatePromoCode(promoCode.trim());
            setPromoValidation(result);
        } finally {
            setPromoValidating(false);
        }
    }, [promoCode, validatePromoCode]);

    /**
     * Handle starting a trial for a specific plan
     */
    const handleStartTrial = useCallback(async (plan) => {
        try {
            setProcessingPlan(plan.id);
            setError(null);

            const appliedPromo = promoValidation?.valid ? promoCode.trim() : null;
            await startTrial(plan.priceId, appliedPromo);
            await refreshSubscriptionStatus();

            const days = promoValidation?.valid ? promoValidation.bonusDays : 30;
            alert(t('plansPage.trialStarted', { days, plan: plan.name }));
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('plansPage.trialFailed'));
        } finally {
            setProcessingPlan(null);
        }
    }, [startTrial, refreshSubscriptionStatus, navigate, promoCode, promoValidation]);

    /**
     * Handle plan selection (purchase or change)
     * Uses unified upgradePlan endpoint that handles both checkout and plan change scenarios
     */
    const handleSelectPlan = useCallback(async (plan) => {
        try {
            setProcessingPlan(plan.id);
            setError(null);

            const isActivePlan = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE;
            const isTrialPlan = subscriptionData?.status === SUBSCRIPTION_STATUS.TRIAL;
            const isFree = plan.isFree || plan.price === 0;
            const hasStripeSubscription = subscriptionData?.hasStripeSubscription;

            // Free plan - use activateFree (permanent, no trial consumption)
            if (isFree) {
                // Only activate if not already on a subscription
                if (!isActivePlan && !isTrialPlan) {
                    await activateFree();
                    await refreshSubscriptionStatus();
                    alert(t('plansPage.freePlanActivated'));
                    navigate('/dashboard');
                }
                setProcessingPlan(null);
                return;
            }

            // Paid plan purchase (no existing Stripe subscription) - show billing preference modal
            // This applies to users on FREE plan or with no subscription who want to BUY (not trial)
            if (!hasStripeSubscription && !isTrialPlan) {
                setSelectedPlanForPurchase(plan);
                setShowBillingModal(true);
                setProcessingPlan(null);
                return;
            }

            // Paid plan - use unified upgradePlan endpoint (for users with existing Stripe subscription)
            // Backend automatically decides: upgrade or scheduled downgrade
            const result = await upgradePlan(plan.priceId);

            // If redirecting to checkout, the page will navigate away
            if (result.redirecting) {
                return;
            }

            // Handle different result types
            if (result.action === 'PLAN_CHANGED') {
                // Immediate upgrade
                alert(t('plansPage.planChanged'));
                await refreshSubscriptionStatus();
            } else if (result.action === 'DOWNGRADE_SCHEDULED') {
                // Downgrade scheduled for end of billing period
                const changeDate = result.scheduledChangeDate
                    ? new Date(result.scheduledChangeDate).toLocaleDateString(t('subscription:_locale', { defaultValue: 'pl-PL' }))
                    : t('plansPage.downgradeScheduledFallback');
                alert(t('plansPage.downgradeScheduled', { date: changeDate }));
                await refreshSubscriptionStatus();
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('plansPage.requestFailed'));
        } finally {
            setProcessingPlan(null);
        }
    }, [subscriptionData, upgradePlan, activateFree, refreshSubscriptionStatus, navigate, t]);

    /**
     * Handle billing preference confirmation from modal
     * Called after user selects invoice/personal billing and confirms data
     */
    const handleBillingConfirm = useCallback(async (billingData) => {
        if (!selectedPlanForPurchase) return;

        try {
            setShowBillingModal(false);
            setProcessingPlan(selectedPlanForPurchase.id);
            setError(null);

            // Store billing preference for potential use in checkout
            // The backend can use this information if needed
            sessionStorage.setItem('billingPreference', JSON.stringify(billingData));

            // Proceed with purchase
            const result = await upgradePlan(selectedPlanForPurchase.priceId);

            // If redirecting to checkout, the page will navigate away
            if (result.redirecting) {
                return;
            }

            // Handle different result types (should typically redirect to checkout for new purchases)
            if (result.action === 'PLAN_CHANGED') {
                alert(t('plansPage.planChanged'));
                await refreshSubscriptionStatus();
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('plansPage.requestFailed'));
        } finally {
            setProcessingPlan(null);
            setSelectedPlanForPurchase(null);
        }
    }, [selectedPlanForPurchase, upgradePlan, refreshSubscriptionStatus, t]);

    /**
     * Handle billing modal close
     */
    const handleBillingModalClose = useCallback(() => {
        setShowBillingModal(false);
        setSelectedPlanForPurchase(null);
        setProcessingPlan(null);
    }, []);

    /**
     * Handle opening customer portal
     */
    const handleOpenPortal = useCallback(async () => {
        try {
            setLoading(true);
            await openCustomerPortal();
        } catch (err) {
            setError(t('plansPage.portalFailed'));
        } finally {
            setLoading(false);
        }
    }, [openCustomerPortal]);

    /**
     * Check if plan is the current active plan
     */
    const isCurrentPlan = (plan) => {
        if (!subscriptionData) return false;
        const isActiveOrTrial =
            subscriptionData.status === SUBSCRIPTION_STATUS.ACTIVE ||
            subscriptionData.status === SUBSCRIPTION_STATUS.TRIAL;
        if (!isActiveOrTrial) return false;

        // Prefer plan code match (stable across Stripe price ID changes)
        if (subscriptionData.planCode && plan.code) {
            return subscriptionData.planCode === plan.code;
        }

        // Fallback for older responses without planCode
        if (plan.isFree || plan.price === 0) {
            return (
                !subscriptionData.priceId &&
                subscriptionData.status === SUBSCRIPTION_STATUS.ACTIVE &&
                !subscriptionData.hasStripeSubscription
            );
        }
        return subscriptionData.priceId === plan.priceId;
    };

    /**
     * Clear error message
     */
    const handleClearError = () => {
        setError(null);
        clearError();
    };

    // Loading state
    if (loading && plans.length === 0) {
        return (
            <div className="subscription-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>{t('plansPage.loadingPlans')}</p>
                </div>
            </div>
        );
    }

    const hasActiveSubscription = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE ||
                                   subscriptionData?.status === SUBSCRIPTION_STATUS.TRIAL;
    const userCanStartTrial = canStartTrial();

    return (
        <div className="subscription-container">
            {/* Header */}
            <header className="subscription-header">
                <button
                    className="back-btn"
                    onClick={() => navigate('/dashboard')}
                >
                    ←
                </button>
                <h1>{t('plansPage.title')}</h1>
                <p>{t('plansPage.subtitle')}</p>
            </header>

            {/* Trial info banner */}
            {userCanStartTrial && (
                <div className="trial-info-banner">
                    <span className="trial-icon">🎉</span>
                    <p dangerouslySetInnerHTML={{ __html: t('plansPage.trialBanner', { days: promoValidation?.valid ? t('plansPage.trialDays', { days: promoValidation.bonusDays }) : t('plansPage.trialDays', { days: 30 }) }) }} />
                </div>
            )}

            {/* Promo code section */}
            {userCanStartTrial && (
                <div className="promo-code-section">
                    <div className="promo-code-input-row">
                        <input
                            type="text"
                            className="promo-code-input"
                            placeholder={t('plansPage.promoPlaceholder')}
                            value={promoCode}
                            onChange={(e) => {
                                setPromoCode(e.target.value);
                                if (promoValidation) setPromoValidation(null);
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && handleValidatePromoCode()}
                        />
                        <button
                            className="promo-code-btn"
                            onClick={handleValidatePromoCode}
                            disabled={!promoCode.trim() || promoValidating}
                        >
                            {promoValidating ? t('plansPage.promoValidating') : t('plansPage.promoApply')}
                        </button>
                    </div>
                    {promoValidation && (
                        <p className={`promo-code-message ${promoValidation.valid ? 'success' : 'error'}`}>
                            {promoValidation.message}
                        </p>
                    )}
                </div>
            )}

            {/* Error banner */}
            {error && (
                <div className="error-banner">
                    <p>{error}</p>
                    <button onClick={handleClearError}>×</button>
                </div>
            )}

            {/* Current subscription status */}
            <CurrentSubscriptionCard
                subscriptionData={subscriptionData}
                isInGracePeriod={isInGracePeriod()}
                onOpenPortal={handleOpenPortal}
                getStatusText={getSubscriptionStatusText}
                formatDate={formatDate}
            />

            {/* Plans carousel */}
            {plans.length === 0 ? (
                <div className="empty-state">
                    <p>{t('plansPage.noPlans')}</p>
                    <button onClick={loadPlans}>{t('common:refresh')}</button>
                </div>
            ) : (
                <div className="plans-grid">
                    {plans.map((plan) => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            isCurrentPlan={isCurrentPlan(plan)}
                            isProcessing={processingPlan === plan.id}
                            hasActiveSubscription={hasActiveSubscription}
                            canStartTrial={userCanStartTrial}
                            onSelect={handleSelectPlan}
                            onStartTrial={handleStartTrial}
                            trialDays={promoValidation?.valid ? promoValidation.bonusDays : 30}
                        />
                    ))}
                </div>
            )}

            {/* Billing Preference Modal */}
            <BillingPreferenceModal
                isOpen={showBillingModal}
                onClose={handleBillingModalClose}
                onConfirm={handleBillingConfirm}
                planName={selectedPlanForPurchase?.name || ''}
                planPrice={selectedPlanForPurchase?.priceFormatted || selectedPlanForPurchase?.price + ' zł/mies.'}
            />
        </div>
    );
};

export default SubscriptionPlans;
